import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../context/SettingsProvider";
import { useToast } from "../context/ToastProvider";
import "../contact/ContactPage.css";

function ReportBugPage() {
    const { settings } = useSettings();
    const { addToast } = useToast();
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        bugType: "",
        description: "",
        steps: "",
        expectedBehavior: "",
        actualBehavior: ""
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            addToast("Bug report submitted successfully! We'll look into it.", "success");
            setFormData({
                name: "",
                email: "",
                bugType: "",
                description: "",
                steps: "",
                expectedBehavior: "",
                actualBehavior: ""
            });
        } catch (error) {
            addToast("Failed to submit bug report. Please try again.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="main-content">
            <Helmet>
                <title>Report a Bug - {settings?.site_name || "Play Store"}</title>
                <meta name="description" content="Report bugs and issues to help us improve" />
            </Helmet>

            <div className="contact-container">
                <div className="contact-header">
                    <h1>Report a Bug</h1>
                    <p>Help us improve by reporting any bugs or issues you encounter</p>
                </div>

                <div className="contact-content">
                    <div className="contact-form-section">
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-group">
                                <label htmlFor="name">Your Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="your.email@example.com"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="bugType">Bug Type *</label>
                                <select
                                    id="bugType"
                                    name="bugType"
                                    value={formData.bugType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select bug type</option>
                                    <option value="ui">UI/Display Issue</option>
                                    <option value="functionality">Functionality Not Working</option>
                                    <option value="performance">Performance Issue</option>
                                    <option value="crash">App Crash</option>
                                    <option value="security">Security Concern</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Bug Description *</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    required
                                    rows="4"
                                    placeholder="Briefly describe the bug..."
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="steps">Steps to Reproduce</label>
                                <textarea
                                    id="steps"
                                    name="steps"
                                    value={formData.steps}
                                    onChange={handleChange}
                                    rows="4"
                                    placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="expectedBehavior">Expected Behavior</label>
                                <textarea
                                    id="expectedBehavior"
                                    name="expectedBehavior"
                                    value={formData.expectedBehavior}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="What should happen?"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="actualBehavior">Actual Behavior</label>
                                <textarea
                                    id="actualBehavior"
                                    name="actualBehavior"
                                    value={formData.actualBehavior}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="What actually happens?"
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="submit-btn"
                                disabled={submitting}
                            >
                                {submitting ? "Submitting..." : "Submit Bug Report"}
                            </button>
                        </form>
                    </div>

                    <div className="contact-info-section">
                        <div className="info-card">
                            <div className="info-icon">🐛</div>
                            <h3>Bug Reporting Tips</h3>
                            <ul style={{ textAlign: "left", lineHeight: "1.8" }}>
                                <li>Be as specific as possible</li>
                                <li>Include steps to reproduce</li>
                                <li>Mention your device/browser</li>
                                <li>Add screenshots if possible</li>
                            </ul>
                        </div>

                        <div className="info-card">
                            <div className="info-icon">⚡</div>
                            <h3>Response Time</h3>
                            <p>We typically respond to bug reports within 24-48 hours</p>
                        </div>

                        <div className="info-card">
                            <div className="info-icon">🔒</div>
                            <h3>Security Issues</h3>
                            <p>For security vulnerabilities, please email us directly at security@{settings?.site_name?.toLowerCase() || "example"}.com</p>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default ReportBugPage;
